/**
 * Experience BOSCO Capture — demo script
 *
 * Presentation-agnostic data for the interactive landing-page demo. Keeping
 * all copy + timings out of the component makes it trivial to swap the
 * scripted variant for a real BOSCO Capture session in the future without
 * touching any JSX.
 *
 * Each step declares:
 *   • `id`       — stable identifier used by the step machine
 *   • `phase`    — coarse phase label ("scan" | "understand" | "review" | "kit" | "generate" | "done")
 *   • `title`    — headline copy for the caption panel
 *   • `subtitle` — optional supporting copy
 *   • `items[]`  — status messages that fade in one after the other
 *   • `duration` — total ms spent on the step (evenly split across items)
 *
 * All durations are the *comfortable* variant; the component halves them
 * when `prefers-reduced-motion: reduce` is set.
 */

export const DEMO_STEPS = [
  {
    id: 'scan',
    phase: 'scan',
    title: 'BOSCO analyses the page',
    subtitle: 'Frame quality, layout and content signals are checked live.',
    duration: 4200,
    items: [
      { label: 'Page aligned',           iconTone: 'ok' },
      { label: 'Lighting sufficient',    iconTone: 'ok' },
      { label: 'Text detected',          iconTone: 'ok' },
      { label: 'Images detected',        iconTone: 'ok' },
      { label: 'Tables detected',        iconTone: 'ok' },
      { label: 'Questions detected',     iconTone: 'ok' },
      { label: 'Page quality excellent', iconTone: 'green' },
    ],
  },
  {
    id: 'understand',
    phase: 'understand',
    title: 'BOSCO understood',
    subtitle:
      'Educational understanding — beyond OCR. Structure, intent and curriculum context are inferred.',
    duration: 4600,
    items: [
      { label: 'Chapter detected' },
      { label: 'Topics identified' },
      { label: 'Key concepts recognised' },
      { label: 'Learning objectives extracted' },
      { label: 'Exercises recognised' },
      { label: 'Diagrams detected' },
      { label: 'Tables recognised' },
      { label: 'Keywords identified' },
      { label: 'Assessment questions recognised' },
    ],
  },
  {
    id: 'review',
    phase: 'review',
    title: 'Review before generating',
    subtitle: 'You remain in control. Review what BOSCO detected before generating resources.',
    duration: 3200,
    items: [
      { label: 'Chapter',             detail: 'Photosynthesis' },
      { label: 'Topics',              detail: 'Light reaction · Dark reaction · Chlorophyll' },
      { label: 'Subtopics',           detail: 'Factors affecting rate · Adaptations' },
      { label: 'Exercises',           detail: '8 short answer · 2 diagram-based' },
      { label: 'Keywords',            detail: 'chlorophyll · ATP · glucose · stomata' },
      { label: 'Diagrams',            detail: 'Cross-section of a leaf' },
      { label: 'Learning objectives', detail: '4 outcomes extracted' },
      { label: 'Concepts',            detail: '12 identified' },
    ],
  },
  {
    id: 'kit',
    phase: 'kit',
    title: 'Pick your Teaching Kit',
    subtitle: 'Every resource is generated from what BOSCO understood — nothing invented.',
    duration: 5000,
    // Kit items mirror the shape of the real /bosco-capture/kit picker so
    // the visual language stays consistent between demo and product.
    items: [
      { id: 'lesson_plan',         label: 'Lesson Plan',            checked: true  },
      { id: 'presentation',        label: 'PowerPoint',             checked: true  },
      { id: 'worksheet',           label: 'Worksheets',             checked: true  },
      { id: 'hots',                label: 'HOTS Questions',         checked: true  },
      { id: 'blooms',              label: "Bloom's Taxonomy Set",   checked: true  },
      { id: 'question_bank',       label: 'Question Bank',          checked: false },
      { id: 'quiz',                label: 'Quiz',                   checked: true  },
      { id: 'exit_ticket',         label: 'Exit Ticket',            checked: false },
      { id: 'rubric',              label: 'Rubrics',                checked: true  },
      { id: 'practical',           label: 'Practical Activities',   checked: false },
      { id: 'viva',                label: 'Viva Questions',         checked: false },
      { id: 'teacher_notes',       label: 'Teacher Notes',          checked: true  },
      { id: 'remedial',            label: 'Remedial Resources',     checked: false },
      { id: 'enrichment',          label: 'Enrichment Activities',  checked: false },
      { id: 'assignment',          label: 'Assignment',             checked: true  },
      { id: 'homework',            label: 'Homework',               checked: false },
      { id: 'answer_key',          label: 'Answer Key',             checked: true  },
      { id: 'discussion',          label: 'Classroom Discussion',   checked: false },
      { id: 'board',               label: 'Board Questions',        checked: false },
      { id: 'assessment',          label: 'Assessment',             checked: true  },
    ],
  },
  {
    id: 'generate',
    phase: 'generate',
    title: 'Generating classroom resources…',
    subtitle: 'Curriculum-aligned outputs assembled from the validated understanding.',
    duration: 5200,
    items: [
      { label: 'Lesson Plan',    ready: false },
      { label: 'PPT',            ready: false },
      { label: 'Worksheet',      ready: false },
      { label: 'HOTS',           ready: false },
      { label: 'Rubrics',        ready: false },
      { label: 'Assessment',     ready: false },
    ],
    finaleLabel: 'Generation Complete',
  },
  {
    id: 'done',
    phase: 'done',
    title: 'From one textbook page to an entire classroom teaching kit.',
    subtitle:
      'BOSCO understands educational content and transforms it into curriculum-ready teaching resources.',
    duration: 0,
    items: [],
  },
];

/**
 * The educational-intelligence pillars shown as a fixed rail on the
 * caption panel — reminds the viewer that BOSCO understands MEANING,
 * not just printed text.
 */
export const INTELLIGENCE_PILLARS = [
  'Educational structure',
  'Learning objectives',
  'Exercises',
  'Diagrams',
  'Assessment patterns',
  'Teacher intent',
  'Curriculum alignment',
];

/**
 * Mock textbook page content driving the SVG render. Kept as data so a
 * future variant (different subject / chapter) can be dropped in without
 * touching the SVG.
 */
export const MOCK_TEXTBOOK_PAGE = {
  header: 'Chapter 6',
  title: 'Photosynthesis',
  intro:
    'Photosynthesis is the process by which green plants and some other organisms use sunlight to synthesise foods with the help of chlorophyll.',
  subheading: '6.1 The Site of Photosynthesis',
  paragraphs: [
    'Photosynthesis primarily occurs in the leaves of plants. Chloroplasts, found abundantly in leaf cells, contain the pigment chlorophyll, which absorbs light energy.',
    'The overall reaction can be summarised by the equation shown below. Light energy is converted into chemical energy stored in glucose molecules.',
  ],
  equation: '6 CO\u2082 + 6 H\u2082O  →  C\u2086H\u2081\u2082O\u2086 + 6 O\u2082',
  exercisesLabel: 'Exercises',
  exercises: [
    'What is the role of chlorophyll in photosynthesis?',
    'Distinguish between light and dark reactions.',
    'Draw and label a cross-section of a leaf.',
  ],
};
